import { Modal } from 'components/Modal/Modal';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ReactComponent as LOADoc } from 'assets/images/LOA.svg';
import FormSection from 'components/FormSection';
import { Controller, useForm } from 'react-hook-form';
import { FileUpload } from 'components/V2/FileUpload/FileUpload';
import { useState } from 'react';
import { LOAModalForm, LOAModalSchema } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import Divider from 'components/Divider';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import Checkbox from 'components/Checkbox';
import { useMutation } from 'react-query';
import SignaturePad from './SignaturePad';

interface LOAModalProps {
  isOpen: boolean;
  close: () => void;
  onConfirm?: () => void;
}

const LOAModal = ({ isOpen, close, onConfirm }: LOAModalProps) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };
  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    reset,
  } = useForm<LOAModalForm>({
    resolver: zodResolver(LOAModalSchema),
  });

  const { mutate } = useMutation(
    async (data: LOAModalForm) => {
      console.log('Submitting LOA...', data);
      return Promise.resolve();
    },
    {
      onSuccess: () => {
        close();
        onConfirm?.();
        reset({});
      },
    },
  );

  const onSubmit = (data: LOAModalForm) => {
    mutate(data);
  };

  return (
    <Modal
      isVisible={isOpen}
      close={close}
      className="!max-h-full !w-[836px] justify-between sm:!max-h-[90vh]"
      scrollableClassName="min-h-full"
      enableScrolling
    >
      <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6 font-bold leading-9">
        <h1 className="text-xl text-content-base-default lg:text-2xl">
          Letter of authorization
        </h1>
        <h1 className="text-base text-content-base-subdued">
          By signing below, you are authorizing Granite to act on your behalf to
          access and make changes to your customer service records with current
          providers.
        </h1>
      </div>
      <form
        className="flex flex-col gap-8 p-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col items-start gap-6 self-stretch">
          <LOADoc />
          <FormSection gridClassName="grid-cols-1" title="Add company logo">
            <Controller
              name="attachments"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FileUpload
                  label="Upload logo (optional)"
                  className="col-span-2"
                  accept=".png,.jpg,.jpeg,image/*"
                  error={errors.attachments?.message}
                  value={value}
                  onChange={onChange}
                  buttonVariant="secondary"
                />
              )}
            />
            <span className="text-sm text-content-base-subdued">
              Adding a logo can enhance the Letter of Authorization and help
              streamline the consolidation process.
            </span>
          </FormSection>
          <div className="self-stretch">
            <Divider layout="horizontal" variant="subdued" />
          </div>
          <FormSection
            gridClassName="grid-cols-2"
            title="Add signature"
            className="w-full"
          >
            <GraniteInput
              {...register(`full_name`)}
              label="Full name"
              error={errors.full_name?.message}
              className="col-span-1"
              innerInputClassName="!w-full !box-border"
            />
            <Controller
              name="signature"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SignaturePad
                  value={value}
                  onChange={onChange}
                  label="Signature"
                  error={errors.signature?.message}
                />
              )}
            />
          </FormSection>
        </div>
        <Checkbox
          label="By checking this box and signing above, I (1) authorize Granite to distribute the Letter of Authorization to my current providers, as needed, and to access and/or make changes to customer service records, effective immediately, and (2) represent and warrant that I am a representative of Customer authorized to act on Customer’s behalf."
          checked={isChecked}
          onChange={handleCheckboxChange}
        />

        <div className="flex items-start gap-4">
          <GraniteButton
            size="large"
            onClick={close}
            variant="secondary"
            type="button"
          >
            Back
          </GraniteButton>
          <GraniteButton
            size="large"
            variant="primary"
            type="submit"
            disabled={!isChecked}
          >
            Sign document
          </GraniteButton>
        </div>
      </form>
    </Modal>
  );
};

export default LOAModal;
